import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
} from '@mui/material';

const PoolDetailsModal = ({ open, pool, onClose }) => {
  if (!pool) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'closed': return 'warning';
      case 'purchased': return 'info';
      case 'in_warehouse': return 'primary';
      case 'in_transit': return 'secondary';
      case 'in_nigeria': return 'info';
      case 'fulfilled': return 'success';
      case 'refund': return 'error';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatMoney = (value, prefix = '¥') => (
    `${prefix}${parseFloat(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  );

  const minimumQuantity = pool.minimum_viable_quantity || pool.target_quantity || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Pool Details: {pool.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Pool Information Grid */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Pool Information
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Initiator:</strong> {pool.initiator?.username || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{' '}
                    <Chip
                      label={pool.status}
                      color={getStatusColor(pool.status)}
                      size="small"
                      variant="outlined"
                    />
                  </Typography>
                  <Typography variant="body2">
                    <strong>Max Participants:</strong> {pool.max_participants}
                  </Typography>
                  {pool.detail_url && (
                    <Typography variant="body2">
                      <strong>Detail URL:</strong>{' '}
                      <a
                        href={pool.detail_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        {pool.detail_url}
                      </a>
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Created:</strong> {new Date(pool.created_at).toLocaleString()}
                  </Typography>
                  {pool.expiration_date && (
                    <Typography variant="body2">
                      <strong>Expires:</strong> {new Date(pool.expiration_date).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Progress & Pricing
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Progress:</strong> {pool.current_quantity}/{minimumQuantity}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={minimumQuantity > 0
                      ? Math.min(100, (pool.current_quantity / minimumQuantity) * 100)
                      : 0}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  />
                  <Typography variant="body2">
                    <strong>Original Price:</strong> {formatMoney(pool.original_price)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Current Unlocked Price:</strong> {formatMoney(pool.current_unit_price || pool.discount_price || pool.original_price)}
                  </Typography>
                  {pool.discount_price && (
                    <Typography variant="body2">
                      <strong>Discount Price:</strong> {formatMoney(pool.discount_price)}
                    </Typography>
                  )}
                  {pool.is_featured && (
                    <Box sx={{ mt: 1 }}>
                      <Chip label="Featured" color="warning" size="small" />
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Participants Section */}
            {pool.participants && pool.participants.length > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Participants ({pool.participants.length})
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {pool.participants.slice(0, 10).map((participant, index) => (
                      <Box key={participant.id || index} sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {participant.user?.username || 'Anonymous'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {participant.quantity} items
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Paid {formatMoney(participant.unit_price_paid)} / Eligible {formatMoney(participant.eligible_unit_price)}
                          {parseFloat(participant.refund_paid_ngn || 0) > 0
                            ? ` / Refunded ${formatMoney(participant.refund_paid_ngn, '₦')}`
                            : ''}
                        </Typography>
                      </Box>
                    ))}
                    {pool.participants.length > 10 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        And {pool.participants.length - 10} more participants...
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PoolDetailsModal;
